import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import Sidebar from './components/Sidebar';
import MessageList from './components/MessageList';
import ChatInput from './components/ChatInput';
import ChatLayout from './components/ChatLayout';
import EditModal from './components/EditModal';
import ChatSearchDialog from './components/ChatSearchDialog';
import { useMessages } from './hooks/useMessages';
import { useChatInput } from './hooks/useChatInput';
import { useSidebar } from './hooks/useSidebar';
import { useChatList } from './hooks/useChatList';

const Chat: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const { isOpen: isSidebarOpen, toggle: toggleSidebar } = useSidebar();
  const {
    chats,
    isLoading: isLoadingChats,
    isLoadingMore,
    hasMore,
    refreshChatList,
    loadMoreChats,
    unreadChatIds,
    markChatUnread,
    markChatRead
  } = useChatList();

  const {
    messages,
    isLoading,
    scrollContainerRef,
    handleStop,
    addUserMessage,
    clearMessages,
    hasConversation,
    retryMessage,
    editMessage,
    editingIndex,
    editingContent,
    setEditingContent,
    cancelEdit,
    submitEdit,
    currentChatId,
    loadChatHistory,
    usageInfo,
    submitFeedback,
  } = useMessages(chatId || null, {
    onConversationUpdated: refreshChatList,
    onMessageForInactiveChat: markChatUnread
  });

  useEffect(() => {
    if (chatId) {
      markChatRead(chatId);
    }
  }, [chatId, markChatRead]);

  // Handle navigation when chatId in URL changes
  useEffect(() => {
    if (chatId && chatId !== currentChatId) {
      loadChatHistory(chatId);
    }
  }, [chatId]);

  const handleSubmit = async () => {
    // Don't submit if limit exceeded
    if (usageInfo?.limitExceeded) {
      return;
    }

    const message = chatInputProps.getCurrentInput();
    if (message) {
      addUserMessage(message, {
        onChatIdReceived: async (newChatId: string) => {
          // Refresh chat list before navigating to ensure sidebar shows correct data
          try {
            await refreshChatList();
          } catch (e) {
            console.error('Failed to refresh chat list:', e);
          }
          // Navigate to the chat messages page when we get a chat_id
          navigate(`/chat/messages/${newChatId}`);
        },
        onFinished: () => {
          refreshChatList();
        }
      });
    }
  };

  const chatInputProps = useChatInput(handleSubmit);

  // Handle quick question button clicks
  const handleQuickQuestionClick = async (question: string) => {
    // Don't process if limit exceeded
    if (usageInfo?.limitExceeded) {
      return;
    }

    addUserMessage(question, {
      onChatIdReceived: async (newChatId: string) => {
        // Refresh chat list before navigating to ensure sidebar shows correct data
        try {
          await refreshChatList();
        } catch (e) {
          console.error('Failed to refresh chat list:', e);
        }
        // Navigate to the chat messages page when we get a chat_id
        navigate(`/chat/messages/${newChatId}`);
      },
      onFinished: () => {
        refreshChatList();
      }
    });
  };

  // Handle new chat button click
  const handleNewChat = () => {
    clearMessages();
    navigate('/chat');
  };

  // Handle chat selection from sidebar
  const handleChatSelect = (selectedChatId: string) => {
    navigate(`/chat/messages/${selectedChatId}`);
    markChatRead(selectedChatId);
  };

  const handleChatDeleted = (deletedChatId: string) => {
    if (chatId === deletedChatId) {
      clearMessages();
      navigate('/chat');
    }
    markChatRead(deletedChatId);
  };

  // const handleOpenDashboard = () => {
  //   navigate('/chat-dashboard');
  // };

  return (
    <ChatLayout>
      <div className="flex w-full h-full bg-gray-50 overflow-hidden">
        <Sidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          onNewChat={handleNewChat}
          // onOpenDashboard={handleOpenDashboard}
          chats={chats}
          unreadChatIds={unreadChatIds}
          isLoadingChats={isLoadingChats}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onChatSelect={handleChatSelect}
          selectedChatId={chatId || null}
          onChatUpdate={refreshChatList}
          onLoadMore={loadMoreChats}
          onChatDeleted={handleChatDeleted}
        />

        <div className="flex-1 flex flex-col h-full">
          <div className="flex-1 flex flex-col min-h-0 relative">
            <button
              type="button"
              className="absolute top-4 right-6 z-10 p-2 rounded-full text-gray-500 bg-white shadow hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Open search dialog"
              onClick={() => setIsSearchDialogOpen(true)}
            >
              <FiSearch className="w-5 h-5" />
            </button>

            <MessageList
              messages={messages}
              isLoading={isLoading}
              hasConversation={hasConversation}
              scrollContainerRef={scrollContainerRef}
              onQuickQuestionClick={usageInfo?.limitExceeded ? undefined : handleQuickQuestionClick}
              onRetry={usageInfo?.limitExceeded ? undefined : retryMessage}
              onEdit={usageInfo?.limitExceeded ? undefined : editMessage}
              onFeedback={submitFeedback}
            />

            <ChatInput
              onSubmit={handleSubmit}
              onStop={handleStop}
              hasConversation={hasConversation}
              isLoading={isLoading}
              usageInfo={usageInfo}
              {...chatInputProps}
            />
          </div>
        </div>
      </div>

      {editingIndex !== null && (
        <EditModal
          content={editingContent}
          onChange={setEditingContent}
          onSubmit={submitEdit}
          onCancel={cancelEdit}
        />
      )}

      <ChatSearchDialog
        open={isSearchDialogOpen}
        onClose={() => setIsSearchDialogOpen(false)}
      />
    </ChatLayout>
  );
};

export default Chat;